// Makercut i18n — modular dictionary loader.
//
// Each language lives in src/i18n/locales/<code>.json with a `_meta`
// block (display name, BCP-47 code, text direction, flag emoji) and a
// flat key→string map for UI strings.
//
// `useI18n()` returns:
//   • lang        — current 2-letter key ('pt' | 'en' | 'es' | …)
//   • setLang(c)  — switch language (persisted in localStorage)
//   • t(key)      — translate with PT fallback then key fallback
//   • dir         — 'ltr' | 'rtl' (drives <html dir>)
//   • locale      — full BCP-47 code ('pt-BR', 'zh-CN', …) for SEO
//   • languages   — { code: meta } for the language switcher
//
// Browser language is auto-detected on first visit; the user's choice
// is persisted from there on. We keep a 2-letter internal key but
// expose the full BCP-47 code for SEO/Helmet so search engines and
// hreflang tags get the right region.

import { createContext, useContext, useState, useEffect, useMemo } from 'react';

import pt from './i18n/locales/pt.json';
import en from './i18n/locales/en.json';
import es from './i18n/locales/es.json';
import fr from './i18n/locales/fr.json';
import de from './i18n/locales/de.json';
import it from './i18n/locales/it.json';
import zh from './i18n/locales/zh.json';
import ja from './i18n/locales/ja.json';
import ar from './i18n/locales/ar.json';

const dictionaries = { pt, en, es, fr, de, it, zh, ja, ar };

// Public catalogue used by the language switcher and SEO helpers.
export const LANGUAGES = Object.fromEntries(
  Object.entries(dictionaries).map(([key, dict]) => [key, dict._meta]),
);

// Order in which we offer languages in the switcher (most-spoken first).
export const LANGUAGE_ORDER = ['pt', 'en', 'es', 'fr', 'de', 'it', 'zh', 'ja', 'ar'];

function detectInitialLang() {
  // 1. user-saved preference wins
  try {
    const saved = localStorage.getItem('mc_lang');
    if (saved && dictionaries[saved]) return saved;
  } catch (_) { /* SSR / private mode */ }
  // 2. browser preference (try every navigator.languages entry)
  const candidates = (typeof navigator !== 'undefined' && navigator.languages)
    || (typeof navigator !== 'undefined' && [navigator.language])
    || [];
  for (const raw of candidates) {
    if (!raw) continue;
    const lower = raw.toLowerCase();
    // Map regional variants to our 2-letter keys
    if (lower.startsWith('pt')) return 'pt';
    if (lower.startsWith('en')) return 'en';
    if (lower.startsWith('es')) return 'es';
    if (lower.startsWith('fr')) return 'fr';
    if (lower.startsWith('de')) return 'de';
    if (lower.startsWith('it')) return 'it';
    if (lower.startsWith('zh')) return 'zh';
    if (lower.startsWith('ja')) return 'ja';
    if (lower.startsWith('ar')) return 'ar';
  }
  // 3. fallback for unknown / unsupported locales.
  // English is the lingua franca of the maker / CNC / laser-cut community
  // and matches the language of most of our reference docs and tool labels.
  // Visitors who prefer Portuguese can switch via the language picker and
  // the choice is persisted to localStorage (see step 1 above).
  return 'en';
}

const I18nContext = createContext(null);

export const I18nProvider = ({ children }) => {
  const [lang, setLang] = useState(detectInitialLang);

  useEffect(() => {
    try { localStorage.setItem('mc_lang', lang); } catch (_) {}
    // Drive <html lang> + <html dir> so screen readers, browser UI,
    // CSS :lang() rules and search engines all stay aligned.
    if (typeof document !== 'undefined') {
      const meta = LANGUAGES[lang] || LANGUAGES.en;
      document.documentElement.setAttribute('lang', meta.code);
      document.documentElement.setAttribute('dir', meta.dir || 'ltr');
    }
  }, [lang]);

  const value = useMemo(() => {
    const dict = dictionaries[lang] || dictionaries.en;
    // Fallback chain: current language → English → Portuguese → key.
    // English is the universal default so a German user with a few
    // missing strings sees them in English (not Portuguese), which is
    // far more readable for the average non-native speaker.
    const fallbackEn = dictionaries.en;
    const fallbackPt = dictionaries.pt;
    const meta = LANGUAGES[lang] || LANGUAGES.en;
    return {
      lang,
      setLang,
      t: (key) => dict[key] ?? fallbackEn[key] ?? fallbackPt[key] ?? key,
      dir: meta.dir || 'ltr',
      locale: meta.code,        // BCP-47 ('pt-BR', 'zh-CN', …)
      languages: LANGUAGES,
      order: LANGUAGE_ORDER,
    };
  }, [lang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => useContext(I18nContext);
